import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft, 
  CheckCircle, 
  Loader2, 
  User, 
  Building, 
  Calendar, 
  CreditCard, 
  DollarSign,
  FileCheck,
  LogIn,
  BarChart3
} from "lucide-react";
import { OnboardingData } from "../index";
import { useToast } from "@/hooks/use-toast";
import confetti from 'canvas-confetti';
import { useLocation } from "wouter";
import { useAuth } from "@/providers/auth-provider";

interface ConfirmationProps {
  onNext: () => void;
  onPrevious: () => void;
  onboardingData: OnboardingData;
  updateOnboardingData: (data: Partial<OnboardingData>) => void;
}

export default function Confirmation({
  onNext,
  onPrevious,
  onboardingData,
  updateOnboardingData,
}: ConfirmationProps) {
  const { toast } = useToast();
  const { register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [setupStage, setSetupStage] = useState<string | null>(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [_, navigate] = useLocation();
  const registrationInProgress = useRef(false);
  
  // Setup stages
  const setupStages = [
    { id: "creating-account", label: "Creating your account", icon: User, delay: 2000 },
    { id: "setting-up-bank", label: "Setting up bank connections", icon: CreditCard, delay: 2500 },
    { id: "creating-charts", label: "Creating chart of accounts", icon: BarChart3, delay: 3000 },
    { id: "finalizing", label: "Finalizing your setup", icon: CheckCircle, delay: 2000 },
  ];
  
  const getPlanName = (planId?: string): string => {
    if (!planId) return "ZentroSeed";
    
    switch (planId) {
      case "zentroseed":
        return "ZentroSeed";
      case "zentrosprout":
        return "ZentroSprout";
      case "zentroscale":
        return "ZentroScale";
      default:
        return "ZentroSeed";
    }
  };
  
  const formatDate = (date?: Date | null): string => {
    if (!date) return "Not set";
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Process the setup stages sequentially
  // Reset registration progress tracking when component mounts or unmounts
  useEffect(() => {
    registrationInProgress.current = false;
    return () => {
      registrationInProgress.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (isLoading && !setupComplete) {
      let currentStageIndex = 0;
      
      const processNextStage = async () => {
        if (currentStageIndex < setupStages.length) {
          const currentStage = setupStages[currentStageIndex];
          setSetupStage(currentStage.id);
          
          // If this is the account creation stage, actually register the user
          if (currentStage.id === "creating-account") {
            // Prevent duplicate registration attempts
            if (registrationInProgress.current) {
              console.log("Registration already in progress, skipping duplicate attempt");
              // Continue to next stage
              currentStageIndex++;
              processNextStage();
              return;
            }
            
            try {
              // Mark registration as in progress
              registrationInProgress.current = true;
              
              // Register the real user in the database
              await register({
                email: onboardingData.email,
                password: onboardingData.password,
                firstName: onboardingData.firstName,
                lastName: onboardingData.lastName,
                fullName: onboardingData.fullName
              });
            } catch (error: any) {
              // Reset registration flag on error
              registrationInProgress.current = false;
              
              console.error("Failed to register user:", error);
              
              // Check if it's because email already exists
              const errorMessage = error?.message || "";
              const isEmailExists = errorMessage.includes("Email already exists");
              
              // Show appropriate error message
              toast({
                title: "Account Setup Issue",
                description: isEmailExists 
                  ? "This email is already registered. Please go back and use a different email address or log in to your existing account."
                  : "We couldn't create your account. Please go back and verify your information.",
                variant: "destructive",
              });
              
              // Send user back to account creation step
              onPrevious();
              onPrevious();
              
              // Exit the setup process to avoid duplicate registration attempts
              return;
            }
          }
          
          setTimeout(() => {
            currentStageIndex++;
            processNextStage();
          }, currentStage.delay);
        } else {
          // All stages complete
          setSetupComplete(true);
          
          // Trigger confetti effect
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
          
          // Update onboarding data with completion timestamp
          updateOnboardingData({
            completedAt: new Date(),
          });
          
          toast({
            title: "Setup complete!",
            description: "Your account is ready to use! Welcome to ZentroBooks.",
          });
        }
      };
      
      processNextStage();
    }
  }, [isLoading, setupComplete, register, onboardingData]);
  
  const handleComplete = async () => {
    try {
      setIsLoading(true);
      // The setup process will be handled by the useEffect
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem completing your onboarding. Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };
  
  const handleGoToLogin = () => {
    navigate("/login");
  };
  
  return (
    <div className="max-w-2xl mx-auto">
      {isLoading && !setupComplete ? (
        // Live Setup Progress Display
        <div className="min-h-[70vh] flex flex-col items-center justify-center">
          <div className="w-full max-w-md mx-auto px-4 py-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold">Setting Up Your Account</h2>
              <p className="text-neutral-600 mt-2">Please wait while we prepare everything for you</p>
            </div>
            
            <div className="space-y-6 mt-8">
              {setupStages.map((stage, index) => {
                const isActive = stage.id === setupStage;
                const isCompleted = setupStages.findIndex(s => s.id === setupStage) > index;
                const StageIcon = stage.icon;
                
                return (
                  <div 
                    key={stage.id} 
                    className={`flex items-center p-4 border rounded-lg transition-all duration-300 ${
                      isActive ? "border-primary bg-primary/5" : 
                      isCompleted ? "border-green-500 bg-green-50" : 
                      "border-gray-200"
                    }`}
                  >
                    <div className={`rounded-full p-2 ${
                      isActive ? "bg-primary/10 text-primary" : 
                      isCompleted ? "bg-green-100 text-green-600" : 
                      "bg-gray-100 text-gray-400"
                    }`}>
                      {isActive ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : isCompleted ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <StageIcon className="h-5 w-5" />
                      )}
                    </div>
                    <div className="ml-4 flex-1">
                      <p className={`font-medium ${
                        isActive ? "text-primary" : 
                        isCompleted ? "text-green-600" : 
                        "text-gray-500"
                      }`}>
                        {stage.label}
                      </p>
                      {isActive && (
                        <p className="text-sm text-gray-500 mt-1">In progress...</p>
                      )}
                      {isCompleted && (
                        <p className="text-sm text-green-600 mt-1">Completed</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : setupComplete ? (
        // Setup Complete View
        <div className="min-h-[70vh] flex flex-col items-center justify-center">
          <div className="w-full max-w-md mx-auto px-4 py-8 text-center">
            <div className="rounded-full bg-green-100 p-4 w-20 h-20 mx-auto flex items-center justify-center mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold mb-2">Setup Complete!</h2>
            <p className="text-neutral-600 mb-6">
              Your ZentroBooks account has been set up successfully. You can now log in to start managing your business finances.
            </p>
            
            <div className="mt-8">
              <Button 
                onClick={handleGoToLogin}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 w-full py-6"
                size="lg"
              >
                <LogIn className="mr-2 h-5 w-5" />
                Log in to your account
              </Button>
              
              <div className="text-sm text-neutral-500 mt-4 space-y-2">
                <p>
                  We've sent a welcome email to <span className="font-medium">{onboardingData.email}</span> with important setup information and next steps.
                </p>
                <p>
                  <span className="font-medium">Note:</span> For security reasons, you'll need to log in with your credentials to access your account.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Review & Confirm View
        <div className="space-y-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold">Review & Confirm</h2>
            <p className="mt-2 text-neutral-600">
              Review your information before completing setup
            </p>
          </div>
          
          <div className="space-y-6">
            {/* Account Information */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <User className="text-primary h-5 w-5 mr-2" />
                  <h3 className="text-lg font-medium">Account Information</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Full Name</div>
                    <div className="text-sm font-medium">{onboardingData.fullName}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Email</div>
                    <div className="text-sm font-medium">{onboardingData.email}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Email Notifications</div>
                    <div className="text-sm">
                      {onboardingData.emailNotifications.reportsSummary && <div className="flex items-center"><CheckCircle className="h-3 w-3 text-green-500 mr-1" /> Reports Summary</div>}
                      {onboardingData.emailNotifications.taskReminders && <div className="flex items-center"><CheckCircle className="h-3 w-3 text-green-500 mr-1" /> Task Reminders</div>}
                      {onboardingData.emailNotifications.subscriptionAlerts && <div className="flex items-center"><CheckCircle className="h-3 w-3 text-green-500 mr-1" /> Subscription Alerts</div>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Business Information */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <Building className="text-primary h-5 w-5 mr-2" />
                  <h3 className="text-lg font-medium">Business Information</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Business Name</div>
                    <div className="text-sm font-medium">{onboardingData.businessName || "Not provided"}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Business Type</div>
                    <div className="text-sm font-medium">{onboardingData.businessType || "Not provided"}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Industry</div>
                    <div className="text-sm font-medium">{onboardingData.industry || "Not provided"}</div>
                  </div>
                  {onboardingData.ein && (
                    <div className="grid grid-cols-2 gap-1">
                      <div className="text-sm text-neutral-500">EIN</div>
                      <div className="text-sm font-medium">{onboardingData.ein}</div>
                    </div>
                  )}
                  {onboardingData.address && (
                    <div className="grid grid-cols-2 gap-1">
                      <div className="text-sm text-neutral-500">Address</div>
                      <div className="text-sm font-medium">
                        {[
                          onboardingData.address.street,
                          onboardingData.address.city,
                          onboardingData.address.state,
                          onboardingData.address.zip,
                          onboardingData.address.country
                        ].filter(Boolean).join(", ")}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Financial Setup */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <FileCheck className="text-primary h-5 w-5 mr-2" />
                  <h3 className="text-lg font-medium">Financial Setup</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Fiscal Year End</div>
                    <div className="text-sm font-medium">{formatDate(onboardingData.fiscalYearEnd)}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Accounting Method</div>
                    <div className="text-sm font-medium capitalize">{onboardingData.accountingMethod || "Not set"}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Tax Filing Frequency</div>
                    <div className="text-sm font-medium capitalize">{onboardingData.taxFilingFrequency || "Not set"}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Base Currency</div>
                    <div className="text-sm font-medium">{onboardingData.baseCurrency || "USD"}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Bank Connections */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <CreditCard className="text-primary h-5 w-5 mr-2" />
                  <h3 className="text-lg font-medium">Bank Connections</h3>
                </div>
                
                {onboardingData.connectedAccounts && onboardingData.connectedAccounts.length > 0 ? (
                  <div className="space-y-2">
                    {onboardingData.connectedAccounts.map((account) => (
                      <div key={account.id} className="flex justify-between items-center py-2 border-b last:border-b-0">
                        <div>
                          <div className="text-sm font-medium">{account.name}</div>
                          <div className="text-xs text-neutral-500">{account.type}</div>
                        </div>
                        <div className="text-sm font-medium">{account.balance}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-neutral-500">No bank accounts connected. You can connect them later.</div>
                )}
              </CardContent>
            </Card>
            
            {/* Subscription Plan */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <DollarSign className="text-primary h-5 w-5 mr-2" />
                  <h3 className="text-lg font-medium">Subscription Plan</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Selected Plan</div>
                    <div className="text-sm font-medium">{getPlanName(onboardingData.subscriptionPlanId)}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Trial End Date</div>
                    <div className="text-sm font-medium">{formatDate(onboardingData.trialEndDate)}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-sm text-neutral-500">Billing Cycle</div>
                    <div className="text-sm font-medium capitalize">{onboardingData.billingCycle || "Monthly"}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={onPrevious}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            
            <Button 
              type="button" 
              onClick={handleComplete}
              disabled={isLoading}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Finalizing...
                </>
              ) : (
                <>
                  Complete Setup
                  <CheckCircle className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}